/**
 * Cloud Batch Status Card with expandable item list.
 *
 * Extracted from DocumentUploadWithCloud.tsx to improve modularity.
 * Displays batch upload progress, status chips per item, and cancel controls.
 */

import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Tooltip,
  Typography,
} from "@mui/material";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import ErrorIcon from "@mui/icons-material/Error";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FolderIcon from "@mui/icons-material/Folder";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";

import {
  useBatchStatus,
  useCancelBatchUpload,
  isCloudBatchTerminal,
} from "src/views/cloudFiles/services/CloudUploadServices";
import type {
  CloudBatchSummary,
  CloudBatchItemStatus,
} from "src/types/cloudFiles";

// =============================================================================
// Helpers
// =============================================================================

/**
 * Calculate progress percentage for a batch summary.
 */
export const calculateBatchProgress = (batch: CloudBatchSummary): number => {
  const total = batch.files_discovered ?? 0;
  if (total === 0) return 0;
  const completed = (batch.files_completed ?? 0) + (batch.files_failed ?? 0);
  return Math.round((completed / total) * 100);
};

type CompletionChipColor = "success" | "error" | "warning";

/**
 * Build the big summary chip shown next to the batch title once the
 * batch has reached a terminal state.  Returns null for active batches.
 */
export const getCompletionChip = (
  batch: CloudBatchSummary,
): { color: CompletionChipColor; label: string } | null => {
  if (!isCloudBatchTerminal(batch.status)) return null;
  const completed = batch.files_completed ?? 0;
  const failed = batch.files_failed ?? 0;
  if (batch.status === "cancelled") {
    return { color: "warning", label: "Cancelled" };
  }
  if (batch.status === "failed") {
    return {
      color: "error",
      label: failed > 0 ? `${failed} failed` : "Failed",
    };
  }
  if (batch.status === "completed_with_errors") {
    return {
      color: "error",
      label: failed > 0 ? `${failed} failed` : "Completed with errors",
    };
  }
  if (failed > 0) {
    return { color: "error", label: `${failed} failed` };
  }
  return { color: "success", label: `Completed · ${completed} files` };
};

const getCompletionChipIcon = (
  color: CompletionChipColor,
): React.ReactElement => {
  if (color === "success") return <CheckCircleIcon />;
  if (color === "warning") return <CancelIcon />;
  return <ErrorIcon />;
};

/**
 * Get status chip color and label for an item.
 * Uses processing_status when available for more detailed post-upload status.
 */
export const getItemStatusChip = (
  item: CloudBatchItemStatus,
): {
  color:
    | "default"
    | "primary"
    | "secondary"
    | "error"
    | "info"
    | "success"
    | "warning";
  label: string;
} => {
  if (item.item_type === "folder") {
    switch (item.discovery_status) {
      case "scanning":
        return { color: "info", label: "Scanning..." };
      case "completed":
        return {
          color: "success",
          label: `${item.files_discovered} files found`,
        };
      case "failed":
        return { color: "error", label: "Discovery failed" };
      default:
        return { color: "default", label: "Pending" };
    }
  } else {
    // For files, check processing_status first (if upload completed)
    if (item.processing_status) {
      const ps = item.processing_status;
      if (ps.is_processing) {
        // Show processing progress
        return {
          color: "info",
          label: `${ps.display_status} (${ps.progress_percentage}%)`,
        };
      } else if (ps.status === "failed") {
        return {
          color: "error",
          label: ps.error_message || "Processing failed",
        };
      } else {
        // Ready
        return { color: "success", label: ps.display_status };
      }
    }

    // Fall back to upload_status if no processing_status yet
    switch (item.upload_status) {
      case "uploading":
        return { color: "info", label: "Uploading..." };
      case "completed":
        return { color: "success", label: "Uploaded" };
      case "failed":
        return { color: "error", label: item.error_message || "Upload failed" };
      case "queued":
        return { color: "warning", label: "Queued" };
      default:
        return { color: "default", label: "Pending" };
    }
  }
};

// =============================================================================
// Cloud Batch Status Card
// =============================================================================

interface CloudBatchStatusCardProps {
  batch: CloudBatchSummary;
  datasetId: number;
  /**
   * Called when the user acknowledges a terminal-state batch and wants
   * to clear it from the Recent Imports feed.  When omitted, no dismiss
   * affordance is rendered.
   */
  onDismiss?: (batchId: string) => void;
}

export const CloudBatchStatusCard: React.FC<CloudBatchStatusCardProps> = ({
  batch,
  datasetId,
  onDismiss,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Cancel batch mutation
  const { mutate: cancelBatch, isLoading: isCancelling } =
    useCancelBatchUpload(datasetId);

  // Detailed batch status with items when expanded — SSE handles updates,
  // hook's built-in fallback polls if SSE is unhealthy.
  const { data: detailedBatch } = useBatchStatus(datasetId, batch.batch_id, {
    includeItems: expanded,
    enabled: expanded,
  });

  // Defensive checks for API response shape
  if (!batch) return null;

  const progress = calculateBatchProgress(batch);
  const isComplete = batch.status ? isCloudBatchTerminal(batch.status) : false;
  const completionChip = getCompletionChip(batch);

  // Direct access to flattened fields (CloudBatchSummary format)
  const filesDiscovered = batch.files_discovered ?? 0;
  const filesCompleted = batch.files_completed ?? 0;
  const filesFailed = batch.files_failed ?? 0;
  const statusText = batch.status?.replace(/_/g, " ") ?? "unknown";

  // Calculate currently uploading (total discovered minus completed and failed)
  const filesUploading = Math.max(
    0,
    filesDiscovered - filesCompleted - filesFailed,
  );

  // Border + progress color tracks the completion chip when terminal so the
  // card never under-signals failures (warning border next to error chip).
  const statusColor = isComplete
    ? (completionChip?.color ?? "warning")
    : "info";

  // Get items from detailed batch response
  const items = detailedBatch?.items ?? [];

  return (
    <Paper
      sx={{
        p: 2,
        mb: 2,
        borderLeft: 4,
        borderLeftColor: `${statusColor}.main`,
      }}
      data-testid={`cloud-batch-card-${batch.batch_id}`}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            aria-label={expanded ? "collapse" : "expand"}
            data-testid={`cloud-batch-expand-${batch.batch_id}`}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle2">
                Cloud Import: {statusText}
              </Typography>
              {completionChip && (
                <Chip
                  size="small"
                  icon={getCompletionChipIcon(completionChip.color)}
                  label={completionChip.label}
                  color={completionChip.color}
                  data-testid={`cloud-batch-completion-chip-${batch.batch_id}`}
                />
              )}
            </Box>
            <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
              <Chip
                size="small"
                icon={<CloudUploadIcon />}
                label={`${filesUploading} uploading`}
                color="info"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${filesCompleted} completed`}
                color="success"
                variant="outlined"
              />
              <Chip
                size="small"
                label={`${filesFailed} failed`}
                color="error"
                variant="outlined"
              />
            </Box>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ textAlign: "right" }}>
            <Typography variant="h6">{progress}%</Typography>
            <Typography variant="caption" color="text.secondary">
              {filesUploading} uploading
            </Typography>
          </Box>
          {!isComplete && (
            <Tooltip title="Cancel import">
              <IconButton
                size="small"
                color="error"
                onClick={() => setShowCancelConfirm(true)}
                disabled={isCancelling}
                aria-label="cancel import"
                data-testid={`cloud-batch-cancel-${batch.batch_id}`}
              >
                <CancelIcon />
              </IconButton>
            </Tooltip>
          )}
          {isComplete && onDismiss && (
            <Tooltip title="Dismiss">
              <IconButton
                size="small"
                onClick={() => onDismiss(batch.batch_id)}
                aria-label="dismiss import"
                data-testid={`cloud-batch-dismiss-${batch.batch_id}`}
              >
                <CloseIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>
      <LinearProgress
        variant={isComplete ? "determinate" : "buffer"}
        value={progress}
        valueBuffer={progress + 5}
        color={statusColor}
        sx={{ borderRadius: 1 }}
        data-testid={`cloud-batch-progress-${batch.batch_id}`}
      />

      {/* Expanded items list */}
      <Collapse in={expanded} timeout="auto">
        <Box sx={{ mt: 2, maxHeight: 300, overflow: "auto" }}>
          {items.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
              {expanded ? "Loading items..." : "No items yet"}
            </Typography>
          ) : (
            <List
              dense
              disablePadding
              data-testid={`cloud-batch-items-${batch.batch_id}`}
            >
              {items.map((item) => {
                const statusChip = getItemStatusChip(item);
                return (
                  <ListItem
                    key={item.id}
                    sx={{
                      py: 0.5,
                      borderBottom: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      {item.item_type === "folder" ? (
                        <FolderIcon fontSize="small" color="primary" />
                      ) : (
                        <InsertDriveFileIcon fontSize="small" color="action" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          variant="body2"
                          noWrap
                          sx={{ maxWidth: 300 }}
                        >
                          {item.filename || item.cloud_path.split("/").pop()}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          noWrap
                        >
                          {item.cloud_path}
                        </Typography>
                      }
                    />
                    <Chip
                      size="small"
                      label={statusChip.label}
                      color={statusChip.color}
                      sx={{ ml: 1 }}
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
          {!isComplete && items.length > 0 && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1, fontStyle: "italic" }}
            >
              Discovery is ongoing — more items may appear
            </Typography>
          )}
        </Box>
      </Collapse>

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        aria-labelledby="cancel-batch-dialog-title"
        aria-describedby="cancel-batch-dialog-description"
        data-testid="cloud-batch-cancel-confirm"
      >
        <DialogTitle id="cancel-batch-dialog-title">
          Cancel Cloud Import?
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="cancel-batch-dialog-description">
            Are you sure you want to cancel this cloud import? Files that have
            already been uploaded will remain in the dataset, but pending
            uploads will be stopped.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowCancelConfirm(false)} color="inherit">
            Continue Importing
          </Button>
          <Button
            onClick={() => {
              cancelBatch(batch.batch_id);
              setShowCancelConfirm(false);
            }}
            color="error"
            variant="contained"
            autoFocus
            data-testid="cloud-batch-cancel-confirm-btn"
          >
            Yes, Cancel Import
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
